// MODO GUION — sólo en desarrollo (ADR-2026-09-28-onboarding-v2-etapa-1).
//
// Reproduce un escaneo y un lote grabados, con el ritmo real, SIN tocar el backend ni
// escribir en la base: para diseñar y revisar las animaciones del onboarding de marca
// con una cuenta de verdad sin pisarle la marca. Se prende con
// `localStorage['atlas:onboarding_marca_guion'] = '1'` y en un build de producción no
// existe (`import.meta.env.DEV`). La marca es de ficción.

import type { EscaneoDeMarca, HallazgosDelEscaneo } from './escaneo';
import type { MarcaGuardada } from './marca';
import type { PiezaId } from './piezas';

export const ID_DEL_GUION = 'guion';
export const MARCA_DEL_GUION = 'guion-marca';

export function enGuion(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    return localStorage.getItem('atlas:onboarding_marca_guion') === '1';
  } catch {
    return false;
  }
}

const LOGO =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="240" height="120" viewBox="0 0 240 120"><text x="120" y="78" text-anchor="middle" font-family="Arial Black,Arial,sans-serif" font-weight="900" font-size="54" letter-spacing="-3" fill="#111">NORTE</text></svg>',
  );

const ANALISIS = `### 1. Identidad de marca
Taller Norte hace camisas y básicos de hombre con telas nobles y calce relajado.
### 2. Público objetivo
Hombres de 25 a 40 que buscan prendas simples y bien hechas para usar todos los días. Compran online y valoran la calidad por sobre la tendencia.
### 3. Esencia visual y estética
Fotos de estudio sobre fondos neutros, mucha luz natural y poco adorno. Composiciones limpias con el producto al centro.
### 4. Tono y dirección creativa
Cercano y directo, sin exagerar. Habla de materiales y de calce con frases cortas.
### 5. Posicionamiento competitivo
Calidad de taller a precio de marca online.`;

const HALLAZGOS: Array<{ ms: number; paso: string; datos: HallazgosDelEscaneo }> = [
  {
    ms: 1_200,
    paso: 'scrape_web',
    datos: {
      sitio: {
        url: 'https://tallernorte.com.ar/',
        titulo: 'Taller Norte | Camisas y básicos de hombre',
        descripcion: 'Camisas de lino y algodón hechas en Argentina.',
        nombre: 'Taller Norte',
        imagen: '',
        color_tema: '#1F3A2E',
      },
    },
  },
  { ms: 2_400, paso: 'guardar_logo_web', datos: { logo: LOGO } },
  { ms: 7_500, paso: 'screenshot_web', datos: { captura: '/images/onboarding/rubros/moda/camisa-hombre/redes.jpg' } },
  {
    ms: 16_000,
    paso: 'identidad_web',
    datos: {
      identidad: {
        nombre: 'Taller Norte',
        rubro: 'Moda e Indumentaria',
        colores: [
          { hex: '#1F3A2E', name: 'Verde bosque', role: 'primary' },
          { hex: '#C8A97E', name: 'Arena', role: 'secondary' },
          { hex: '#E4572E', name: 'Ladrillo', role: 'accent' },
          { hex: '#F5F2EC', name: 'Crudo', role: 'neutral' },
          { hex: '#1A1A1A', name: 'Carbón', role: 'neutral' },
        ],
        tipografias: { style: 'Sans humanista, firme y legible, con titulares en mayúsculas', suggested: ['Figtree'] },
        analisis: ANALISIS,
      },
    },
  },
];
const FIN_MS = 18_000;

let inicio = 0;

export function iniciarGuion(): void {
  inicio = Date.now();
}

export function escaneoDelGuion(): EscaneoDeMarca {
  if (!inicio) inicio = Date.now();
  const t = Date.now() - inicio;
  const llegados = HALLAZGOS.filter((h) => h.ms <= t);
  const fin = t >= FIN_MS;
  return {
    estado: fin ? 'listo' : 'analizando',
    pasos: llegados.map((h) => ({ nombre: h.paso, estado: 'ok' as const, ms: 800 })),
    hallazgos: Object.assign({}, ...llegados.map((h) => h.datos)) as HallazgosDelEscaneo,
    marca: fin ? { brand_id: MARCA_DEL_GUION, nombre: 'Taller Norte' } : null,
    error: null,
  };
}

export function marcaDelGuion(): MarcaGuardada {
  const identidad = HALLAZGOS[3]!.datos.identidad!;
  return {
    id: MARCA_DEL_GUION,
    nombre: identidad.nombre,
    rubro: identidad.rubro,
    colores: identidad.colores,
    tipografias: identidad.tipografias,
    logoUrl: LOGO,
    analisis: identidad.analisis,
  };
}

const PIEZAS_DEL_GUION: Array<{ id: PiezaId; ms: number; url: string }> = [
  { id: 'v1', ms: 3_000, url: '/images/onboarding/rubros/moda/camisa-hombre/catalogo.jpg' },
  { id: 'v2', ms: 5_000, url: '/images/onboarding/rubros/moda/camisa-hombre/redes.jpg' },
  { id: 'v3', ms: 6_500, url: '/images/onboarding/rubros/moda/camisa-hombre/fondo-blanco.jpg' },
];

export async function piezasDelGuion(onPieza: (p: { id: PiezaId; imagenId: string; url: string }) => void): Promise<void> {
  const t0 = Date.now();
  for (const p of PIEZAS_DEL_GUION) {
    await new Promise((r) => setTimeout(r, Math.max(0, p.ms - (Date.now() - t0))));
    onPieza({ id: p.id, imagenId: `guion-${p.id}`, url: p.url });
  }
}
